import React from 'react';
import { aboutUs } from '../data/about-us';
import logo from '../assets/logo.jpg'; // or your preferred image
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import AboutSchemaData from '../data/seo/About';

const About: React.FC = () => {
  return (
    <div className="bg-white min-h-screen">
      <SEO
        title={AboutSchemaData.pageTitle}
        description={AboutSchemaData.pageDescription}
        keywords={AboutSchemaData.pageKeywords}
        canonical={AboutSchemaData.canonicalUrl}
        schema={AboutSchemaData.aboutSchemaJson}
        openGraph={
          {
            title: AboutSchemaData.pageTitle,
            description: AboutSchemaData.pageDescription,
            url: AboutSchemaData.canonicalUrl,
            type: 'website',
            image: logo, // Use your logo or a relevant image
          }
        }

      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="flex flex-col md:flex-row gap-12 items-center mb-16">
          <div className="md:w-1/2">
            <div className="text-sm font-medium text-brand-primary mb-4">
              <span className="inline-block px-3 py-1 bg-gray-50 rounded-full border border-gray-100">
                Our Story
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-brand-primary mb-6">
              About MASA HILLS
              <br />
              <span className="text-brand-coral">
                Health Services
              </span>
            </h1>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">{aboutUs.mission}</p>
          </div>
          <div className="md:w-1/2">
            <div className="relative rounded-2xl overflow-hidden shadow-xl">
              <img
                src={logo}
                alt="MASA HILLS Health Services"
                className="w-full h-auto object-cover"
              />
              {/* Brand accent overlay */}
              <div className="absolute inset-0 bg-gradient-to-tr from-brand-coral/10 via-transparent to-brand-teal/10 pointer-events-none"></div>
            </div>
          </div>
        </div>

        {/* Content Sections */}
        <div className="grid md:grid-cols-2 gap-12 mb-24">
          <div className="space-y-8 h-full">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 h-full flex flex-col hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-2xl font-bold text-brand-primary mb-6 flex items-center">
                <span className="w-8 h-8 bg-brand-coral rounded-full flex items-center justify-center text-white mr-4 shadow-sm">
                  ✓
                </span>
                Our Approach
              </h2>
              <div className="space-y-4 text-gray-600 flex-grow">
                {aboutUs.description.slice(0, Math.ceil(aboutUs.description.length / 2)).map((paragraph, idx) => (
                  <p key={idx} className="leading-relaxed">{paragraph}</p>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-8 h-full">
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 h-full flex flex-col hover:shadow-xl transition-shadow duration-300">
              <h2 className="text-2xl font-bold text-brand-primary mb-6 flex items-center">
                <span className="w-8 h-8 bg-brand-teal rounded-full flex items-center justify-center text-white mr-4 shadow-sm">
                  ✓
                </span>
                Our Values
              </h2>
              <div className="space-y-4 text-gray-600 flex-grow">
                {aboutUs.description.slice(Math.ceil(aboutUs.description.length / 2)).map((paragraph, idx) => (
                  <p key={idx} className="leading-relaxed">{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Why Choose Us Section - Brand Colors */}
                {/* Why Choose Us Section - Brand Colors */}
                <div className="bg-gradient-to-br from-masa-blue/5 via-masa-blue/10 to-masa-blue/5 rounded-3xl p-12 mb-24">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Why Choose MASA HILLS Health Services?
            </h2>
            <p className="text-lg text-gray-600">
              Experience home care excellence with our comprehensive range of services and dedicated team of professionals.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: "🏥",
                title: "State-of-the-Art Facilities",
                description: "Modern equipment and comfortable environments for the best client experience."
              },
              {
                icon: "💝",
                title: "Client-Centered Care",
                description: "Personalized treatment plans and compassionate care for every client."
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/80 backdrop-blur-sm p-6 rounded-xl border border-masa-blue/10"
              >
                <div className="text-4xl mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Mission & Vision Cards */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          <div className="bg-gradient-to-br from-brand-coral/5 to-brand-yellow/5 p-8 rounded-xl border border-brand-coral/20">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-brand-coral rounded-lg flex items-center justify-center text-white mr-4">
                🎯
              </div>
              <h3 className="text-xl font-bold text-brand-primary">Our Mission</h3>
            </div>
            <p className="text-gray-600 leading-relaxed">
              To provide compassionate, comprehensive home care services that enhance the quality of life for individuals and families in our community.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-brand-teal/5 to-brand-blue/5 p-8 rounded-xl border border-brand-teal/20">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-brand-teal rounded-lg flex items-center justify-center text-white mr-4">
                👁️
              </div>
              <h3 className="text-xl font-bold text-brand-primary">Our Vision</h3>
            </div>
            <p className="text-gray-600 leading-relaxed">
              To be the leading home care provider recognized for excellence, innovation, and personalized care in our region.
            </p>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-white p-8 rounded-xl shadow-lg border border-gray-100">
          <h3 className="text-2xl font-bold text-brand-primary mb-4">Ready to Experience Our Care?</h3>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto leading-relaxed">
            Contact us today to learn more about our services or schedule a consultation with our experienced home care professionals.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:0014695730176"
              className="px-8 py-3 bg-brand-coral text-white font-semibold rounded-lg shadow-lg hover:shadow-xl hover:bg-brand-teal/90 transition-all duration-300 transform hover:-translate-y-0.5"
            >
              Call Now
            </a>
            <a
              href="/contact"
              className="px-8 py-3 border-2 border-brand-primary text-brand-primary font-semibold rounded-lg hover:bg-brand-primary hover:text-black transition-all duration-300"
            >
              Contact Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;